import React, { useEffect, useMemo, useState } from "react";
import { TextField, Button, Typography, Box, Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Grid2 from "@mui/material/Grid2";
import { useParams } from "react-router-dom";

import { Event } from "api/Interactions/InteractionsInterfaces";
import { eventsAPI } from "api/Interactions/EventsAPI";

import { peopleAPI } from "api/Community/PeopleAPI";
import { addItemsToNodes } from "api/Campaigns/CampaignAPI";

import GenericStaticSummary from "components/Records/RecordDisplay/GenericStaticSummary";
import BasePaper from "components/Base/BasePaper";
import EditableFieldsTable from "components/Records/RecordDisplay/EditableFieldsTable";

const EventRegistration: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Optional: cache people list for dedupe (keeps behavior snappy)
  const [peopleCache, setPeopleCache] = useState<any[] | null>(null);
  const [peopleCacheLoading, setPeopleCacheLoading] = useState(false);

  // Form State
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  // UI State
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const normalizedEmail = useMemo(() => (email || "").trim().toLowerCase(), [email]);

  // Load Event
  useEffect(() => {
    if (!token) return;

    const fetchEvent = async () => {
      try {
        const eventData = await eventsAPI.getByToken(token);
        setEvent(eventData);
      } catch (error) {
        console.error("Failed to fetch event:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [token]);

  // (Optional) Warm people cache so registration can dedupe by email
  useEffect(() => {
    // Only fetch if user has started typing an email (avoids heavy GET /all/)
    if (!normalizedEmail || peopleCache || peopleCacheLoading) return;

    const fetchPeople = async () => {
      setPeopleCacheLoading(true);
      try {
        const all = token
        ? await peopleAPI.getAllWithInteractionToken(token)
        : await peopleAPI.getAll();
        setPeopleCache(all as any[]);
      } catch (err) {
        // Non-fatal; we can still create without dedupe
        console.warn("Could not load people list for dedupe:", err);
      } finally {
        setPeopleCacheLoading(false);
      }
    };

    fetchPeople();
  }, [normalizedEmail, peopleCache, peopleCacheLoading]);

  const getOrCreatePersonId = async (): Promise<number> => {
    if (normalizedEmail) {
      const list =
        peopleCache ??
        (token
          ? await peopleAPI.getAllWithInteractionToken(token)
          : await peopleAPI.getAll());

      if (!peopleCache) setPeopleCache(list as any[]);

      const existing = (list as any[]).find((p) => {
        const pEmail = (p?.email || "").trim().toLowerCase();
        return pEmail && pEmail === normalizedEmail;
      });

      if (existing?.id) return Number(existing.id);
    }

    const created = token
      ? await peopleAPI.createWithInteractionToken(
          {
            first_name: firstName,
            last_name: lastName,
            email: email,
          } as any,
          token
        )
      : await peopleAPI.create({
          first_name: firstName,
          last_name: lastName,
          email: email,
        } as any);

    if (!created?.id) throw new Error("Person creation did not return an id");

    if (peopleCache) setPeopleCache([{ ...created }, ...peopleCache]);

    return Number(created.id);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event || submitting) return;

    setSubmitting(true);
    setErrorMessage(null);

    try {
      await getOrCreatePersonId();

      await addItemsToNodes({
        interaction_type: "event",
        interaction_id: event.id,
        item_field_name: "people",
        selected_items: [],
        selected_items_input: [{ first_name: firstName, last_name: lastName, email }],
      });

      // Refresh event (if your event serializer reflects changes via linked node sync)
      if (token) {
        const updatedEvent = await eventsAPI.getByToken(token);
        setEvent(updatedEvent);
      }

      // Reset form + show success UI
      setFirstName("");
      setLastName("");
      setEmail("");
      setRegistrationSuccess(true);
    } catch (error) {
      console.error("Failed to register person:", error);
      setErrorMessage("Registration failed. Please try again.");
      setRegistrationSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <GenericStaticSummary
        record={event}
        titleField="name"
        descriptionField="description"
        loading={loading}
      />

      <Grid2 container spacing={2}>
        {/* LEFT: Registration Form OR Success */}
        <Grid2 size={{ xl: 6, sm: 12 }}>
          <BasePaper>
            {registrationSuccess ? (
              <Box
                sx={{
                  py: 6,
                  px: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 2,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 96, color: "success.main" }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  You’re registered!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  You have been registered for this event.
                </Typography>

                {/* Optional: allow another registration without reloading */}
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    setRegistrationSuccess(false);
                    setErrorMessage(null);
                  }}
                >
                  Register another person
                </Button>
              </Box>
            ) : (
              <>
                {errorMessage && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                  </Alert>
                )}

                <form onSubmit={handleRegister}>
                  <TextField
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                    margin="normal"
                    required
                  />

                  <TextField
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth
                    margin="normal"
                    required
                  />

                  <TextField
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    margin="normal"
                    type="email"
                    required
                    helperText={peopleCacheLoading ? "Checking existing people…" : undefined}
                  />

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    By submitting this form, you acknowledge that your information will be stored and processed by {event?.organisation.name ?? "this organisation"} in accordance with its privacy policy and applicable UK data protection law.
                  </Typography>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={submitting}
                  >
                    {submitting ? "Registering…" : "Register"}
                  </Button>
                </form>
              </>
            )}
          </BasePaper>
        </Grid2>

        {/* RIGHT: Event Data */}
        <Grid2 size={{ xl: 6, sm: 12 }}>
          <BasePaper>
            <EditableFieldsTable
              record={event}
              title="Event Details"
              fields={[
                { field: "location", label: "Location",  type: "text", editable: false },
                { field: "start_time", label: "Start Time", type: "datetime", editable: false },
                { field: "end_time", label: "End Time", type: "datetime", editable: false },
              ]}
              onSave={() => {}}
            />
          </BasePaper>
        </Grid2>
      </Grid2>
    </>
  );
};

export default EventRegistration;
